/**
 * Google Maps Geocoder Utility
 *
 * Uses the Maps JavaScript SDK instead of REST API to work with
 * HTTP referrer-restricted API keys.
 */
window.GoogleMapsGeocoder = window.GoogleMapsGeocoder || (function() {
    let sdkLoadPromise = null;
    let geocoderInstance = null;

    /**
     * Load the Google Maps JavaScript SDK
     * @param {string} apiKey - Google Maps API key
     * @returns {Promise<void>}
     */
    function loadSDK(apiKey) {
        if (sdkLoadPromise) return sdkLoadPromise;

        // Check if SDK is already loaded
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
            geocoderInstance = new window.google.maps.Geocoder();
            sdkLoadPromise = Promise.resolve();
            return sdkLoadPromise;
        }

        sdkLoadPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                if (window.google && window.google.maps) {
                    geocoderInstance = new window.google.maps.Geocoder();
                    resolve();
                } else {
                    reject(new Error('[GoogleMapsGeocoder] SDK loaded but google.maps not available'));
                }
            };

            script.onerror = () => {
                sdkLoadPromise = null;
                reject(new Error('[GoogleMapsGeocoder] Failed to load Google Maps SDK'));
            };

            document.head.appendChild(script);
        });

        return sdkLoadPromise;
    }

    /**
     * Get the geocoder instance, loading SDK if needed
     * @param {string} apiKey - Google Maps API key
     * @returns {Promise<google.maps.Geocoder>}
     */
    async function getGeocoder(apiKey) {
        await loadSDK(apiKey);
        return geocoderInstance;
    }

    /**
     * Parse Google Maps address_components into a flat object
     * @param {Array} components - Address components from geocoder result
     * @returns {Object}
     */
    function parseAddressComponents(components) {
        if (!Array.isArray(components)) return {};

        const parsed = {
            street_number: '',
            route: '',
            locality: '',
            administrative_area_level_1: '',
            administrative_area_level_2: '',
            country: '',
            postal_code: '',
        };

        components.forEach(component => {
            const type = component.types[0];
            if (Object.prototype.hasOwnProperty.call(parsed, type)) {
                parsed[type] = component.long_name || component.short_name;
            }
        });

        return parsed;
    }

    /**
     * Normalize SDK result to match REST API structure
     * The SDK returns lat/lng as methods, REST returns them as numbers
     * @param {google.maps.GeocoderResult} result
     * @returns {Object}
     */
    function normalizeResult(result) {
        const location = result.geometry?.location;
        if (!location) return null;

        // SDK returns lat() and lng() as methods
        const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
        const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

        const addressComponents = parseAddressComponents(result.address_components);

        return {
            latitude: lat,
            longitude: lng,
            formatted_address: result.formatted_address,
            address_components: addressComponents,
            place_id: result.place_id,
            name: addressComponents.locality,
            _raw: result
        };
    }

    /**
     * Geocode an address to coordinates
     * @param {string} address - Address string to geocode
     * @param {string} apiKey - Google Maps API key
     * @returns {Promise<Object|null>} Normalized geocode result
     */
    async function geocodeAddress(address, apiKey) {
        if (!address || !apiKey) {
            console.error('[GoogleMapsGeocoder] Address and API key are required');
            return null;
        }

        try {
            const geocoder = await getGeocoder(apiKey);

            return new Promise((resolve) => {
                geocoder.geocode({ address: address }, (results, status) => {
                    if (status !== 'OK' || !results || results.length === 0) {
                        console.warn('[GoogleMapsGeocoder] Geocoding failed:', status);
                        resolve(null);
                        return;
                    }

                    const normalized = normalizeResult(results[0]);
                    resolve(normalized);
                });
            });
        } catch (error) {
            console.error('[GoogleMapsGeocoder] Error geocoding address:', error);
            return null;
        }
    }

    /**
     * Reverse geocode coordinates to address
     * @param {number} lat - Latitude
     * @param {number} lng - Longitude
     * @param {string} apiKey - Google Maps API key
     * @returns {Promise<Object|null>} Normalized geocode result
     */
    async function reverseGeocode(lat, lng, apiKey) {
        const latitude = Number(lat);
        const longitude = Number(lng);

        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            console.error('[GoogleMapsGeocoder] Invalid coordinates');
            return null;
        }

        if (!apiKey) {
            console.error('[GoogleMapsGeocoder] API key is required');
            return null;
        }

        try {
            const geocoder = await getGeocoder(apiKey);

            return new Promise((resolve) => {
                geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                    if (status !== 'OK' || !results || results.length === 0) {
                        console.warn('[GoogleMapsGeocoder] Reverse geocoding failed:', status);
                        resolve(null);
                        return;
                    }

                    const normalized = normalizeResult(results[0]);
                    resolve(normalized);
                });
            });
        } catch (error) {
            console.error('[GoogleMapsGeocoder] Error reverse geocoding:', error);
            return null;
        }
    }

    /**
     * Calculate distance between two points using Distance Matrix Service
     * @param {Object} origin - {lat: number, lng: number}
     * @param {Object} destination - {lat: number, lng: number}
     * @param {string} apiKey - Google Maps API key
     * @param {Object} options - Optional parameters (mode, avoid, units)
     * @returns {Promise<Object|null>} Distance and duration information
     */
    async function getDistanceMatrix(origin, destination, apiKey, options = {}) {
        if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
            console.error('[GoogleMapsGeocoder] Invalid origin or destination coordinates');
            return null;
        }

        if (!apiKey) {
            console.error('[GoogleMapsGeocoder] API key is required');
            return null;
        }

        try {
            await loadSDK(apiKey);

            const service = new window.google.maps.DistanceMatrixService();

            // Map options to SDK format
            const travelMode = (options.mode || 'driving').toUpperCase();
            const unitSystem = options.units === 'imperial'
                ? window.google.maps.UnitSystem.IMPERIAL
                : window.google.maps.UnitSystem.METRIC;

            const request = {
                origins: [{ lat: origin.lat, lng: origin.lng }],
                destinations: [{ lat: destination.lat, lng: destination.lng }],
                travelMode: window.google.maps.TravelMode[travelMode] || window.google.maps.TravelMode.DRIVING,
                unitSystem: unitSystem
            };

            // Add avoid options if specified
            if (options.avoid) {
                const avoidMap = {
                    tolls: window.google.maps.TravelMode ? true : false,
                    highways: window.google.maps.TravelMode ? true : false,
                    ferries: window.google.maps.TravelMode ? true : false
                };
                // SDK handles avoid differently - via request options
                if (options.avoid === 'tolls') request.avoidTolls = true;
                if (options.avoid === 'highways') request.avoidHighways = true;
                if (options.avoid === 'ferries') request.avoidFerries = true;
            }

            return new Promise((resolve) => {
                service.getDistanceMatrix(request, (response, status) => {
                    if (status !== 'OK') {
                        console.warn('[GoogleMapsGeocoder] Distance Matrix failed:', status);
                        resolve(null);
                        return;
                    }

                    const element = response.rows?.[0]?.elements?.[0];
                    if (!element || element.status !== 'OK') {
                        console.warn('[GoogleMapsGeocoder] No valid route found');
                        resolve(null);
                        return;
                    }

                    resolve({
                        distance: {
                            text: element.distance.text,
                            value: element.distance.value, // in meters
                            kilometers: element.distance.value / 1000,
                            miles: element.distance.value / 1609.34
                        },
                        duration: {
                            text: element.duration.text,
                            value: element.duration.value, // in seconds
                            minutes: element.duration.value / 60
                        },
                        origin: response.originAddresses?.[0] || '',
                        destination: response.destinationAddresses?.[0] || ''
                    });
                });
            });
        } catch (error) {
            console.error('[GoogleMapsGeocoder] Error calculating distance:', error);
            return null;
        }
    }

    // Public API
    return {
        geocodeAddress,
        reverseGeocode,
        getDistanceMatrix
    };
})();
